import React, { FC, useContext, useEffect, useMemo, useCallback } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { SignalRContext } from '../../context/signalrContext'
import { CurrentUserObject } from '../../lib/api/types/user.interfaces'
import { UserService } from '../../lib/api'
import { useAuth } from '../../hooks'
import { useUpdateDocumentCache } from '../../hooks/api/documentHooks'
import { RefreshFilePayload } from '../../lib/api/types/document.interfaces'

export interface AuthLayoutType {
	isAdmin: boolean
	isOwner: boolean
	currentUser: CurrentUserObject
	setCurrentUser: (user: CurrentUserObject) => void
	onUpdateUser: () => void
}

const AuthLayout: FC = () => {
	const { connection, folderScopeId } = useContext(SignalRContext)
	const { refreshFile } = useUpdateDocumentCache()
	const navigate = useNavigate()
	const { currentUser, setCurrentUser, isAdmin, isOwner, isAuthenticated } =
		useAuth()

	useEffect(() => {
		if (!connection) return
		const onFileRefresh = (payload: RefreshFilePayload) => {
			refreshFile(payload, folderScopeId)
		}
		connection.on('refreshFile', onFileRefresh)
		return () => connection.off('refreshFile', onFileRefresh)
	}, [connection, folderScopeId, refreshFile])

	const refreshUserAfterRoleChange = useCallback(async () => {
		try {
			const userResponse = await UserService.getUser()
			if (userResponse?.data) setCurrentUser(userResponse.data)
		} catch {
			// ignore
		}
	}, [setCurrentUser])

	useEffect(() => {
		if (connection) {
			connection.on('userBlocked', () => navigate('/'))
			return () => {
				connection.off('userBlocked')
			}
		}
	}, [connection, navigate])

	useEffect(() => {
		if (!connection || !currentUser?.id) return

		const onRoleChanged = async (payload: { userId?: string } | unknown) => {
			const userId =
				typeof payload === 'object' && payload !== null && 'userId' in payload
					? (payload as { userId: string }).userId
					: (payload as { arguments?: [{ userId?: string }] })?.arguments?.[0]
							?.userId
			if (userId && userId === currentUser.id) {
				await refreshUserAfterRoleChange()
			}
		}

		connection.on('roleChanged', onRoleChanged)
		return () => {
			connection.off('roleChanged')
		}
	}, [connection, currentUser?.id, refreshUserAfterRoleChange])

	return (
		<Outlet
			context={{
				isAdmin,
				isOwner,
				currentUser,
				setCurrentUser,
				onUpdateUser: refreshUserAfterRoleChange,
			}}
		/>
	)
}

export default AuthLayout
